import { getStore } from 'store';
import jwtDecode from 'jwt-decode';
import { showNotification } from 'actions/notifications-actions';
import { NotificationTypes, NotificationTexts } from '../components/Notifications/Notification';
import logger from 'util/logger';

const LOAD_GIGYA_TIMEOUT = 30 * 1000;

export const gigyaScriptLoaded = () => !!(window.gigya && window.gigya.isGigya);

export const loadGigyaScript = (apiKey) =>
  new Promise((resolve) => {
    if (gigyaScriptLoaded()) throw new Error('Gigya script is already loaded!');

    if (!apiKey) throw new Error('Parameter apiKey is invalid!');

    const talpaGigyaModal = document.querySelector('talpa-meer-single-sign-on');
    talpaGigyaModal.config = {
      brand: 'JUKE',
      gigyaScriptUrl: 'https://cdns.eu1.gigya.com/js/gigya.js',
      gigyaApiKey: apiKey,
    };

    resolve(true);
  });

function getGigya() {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    function loop() {
      if (Date.now() - start > LOAD_GIGYA_TIMEOUT) {
        reject(new Error('Failed to load Gigya SDK.'));
      } else if (gigyaScriptLoaded()) {
        resolve(window.gigya);
      } else {
        setTimeout(loop, 100);
      }
    }
    loop();
  });
}

function ensureGigya(getGigyaFunction) {
  return async (...args) => {
    try {
      const gigya = await getGigya();
      const gigyaFunction = getGigyaFunction(gigya);
      return gigyaFunction(...args);
    } catch (e) {
      // Gigya tries to execute a hide() on a element that is undefined,
      // this triggers the snackbar.
      // This is a not so nice fix to prevent triggering the snackbar on this error
      if (e instanceof TypeError) {
        logger.error(e);
        return false;
      }
      getStore().dispatch(
        showNotification({
          type: NotificationTypes.ERROR,
          message: NotificationTexts.GIGYA_LOAD_ERROR,
        })
      );
    }
    return true;
  };
}

/**
 * Stores a Gigya JWT in local storage
 * @param {string} token
 */
export function storeGigyaToken(token) {
  try {
    const TOKEN_KEY = 'juke_gigya_jwt';

    jwtDecode(token);

    localStorage.setItem(TOKEN_KEY, token);
    logger.info('[storeGigyaToken] Storing Gigya JWT to localStorage.', token);
  } catch (err) {
    logger.error('[storeGigyaToken] Error trying to save a Gigya JWT to localStorage.', err);
  }
}

/**
 * Deletes a previously stored JWT
 */
export function clearGigyaToken() {
  const TOKEN_KEY = 'juke_gigya_jwt';

  localStorage.removeItem(TOKEN_KEY);
  logger.info('[storeGigyaToken] Removing Gigya JWT from localStorage.');
}

/**
 * Gets a previously stored JWT
 */
export function getStoredGigyaToken() {
  const TOKEN_KEY = 'juke_gigya_jwt';
  let token;

  // Check if we have a valid stored Gigya token
  token = localStorage.getItem(TOKEN_KEY);

  if (token) {
    // Check if token is still valid
    const decodedToken = jwtDecode(token);

    if (typeof decodedToken.exp !== 'undefined' && Date.now() > decodedToken.exp * 1000) {
      logger.warn('[getStoredGigyaToken] Token has expired.');
      clearGigyaToken();
      token = undefined;
    }

    return token;
  }

  return undefined;
}

/**
 * Promisified wrapper for Gigya's accounts.getJWT() method. The method itself
 * has no return but implements a callback. This will be used to
 * @returns {Promise<string>}
 */
export function fetchGigyaJWTPromise() {
  return new Promise((resolve, reject) => {
    if (gigyaScriptLoaded()) {
      const token = getStoredGigyaToken();

      if (token) {
        resolve(token);
      } else {
        window.gigya.accounts.getJWT({
          expiration: 1 * 60 * 60, // 1 hour
          callback: (response) => {
            logger.info('handling JWT response', response);

            if (typeof response !== 'object') {
              logger.error(
                '[fetchGigyaJWTPromise] Failed fetching a Gigya JWT. Expected a response object from Giya API.'
              );
              reject();
            }

            if (response.errorCode > 0) {
              logger.error(
                `[fetchGigyaJWTPromise] Failed fetching a Gigya JWT. Errorcode${response.errorCode}:${response.errorMessage}`
              );
              reject();
            }

            logger.info('[fetchGigyaJWTPromise] Received Gigya JWT:', response.id_token);
            storeGigyaToken(response.id_token);
            resolve(response.id_token);
          },
        });
      }
    } else {
      logger.error(NotificationTexts.GIGYA_LOAD_ERROR);

      reject();

      getStore().dispatch(
        showNotification({
          type: NotificationTypes.ERROR,
          message: NotificationTexts.GIGYA_LOAD_ERROR,
        })
      );
    }
  });
}

export default {
  getAccountInfo: ensureGigya((gigya) => gigya.ids.getAccountInfo),
  logout: ensureGigya((gigya) => gigya.accounts.logout),
  showScreenSet: ensureGigya((gigya) => gigya.accounts.showScreenSet),
  switchScreen: ensureGigya((gigya) => gigya.accounts.switchScreen),
  hideScreenSet: ensureGigya((gigya) => gigya.accounts.hideScreenSet),
  addEventHandlers: ensureGigya((gigya) => gigya.accounts.addEventHandlers),
  setAccountInfo: ensureGigya((gigya) => gigya.accounts.setAccountInfo),
  clearGigyaToken,
};
